import React, { useState, useEffect, useContext } from 'react';
import { Button } from '@stormwater-software/lib-ui';
import { Table, TableHead, TableRow, TableCell, TableBody, Tooltip } from '@mui/material';
import { FormContextProvider } from '@stormwater-software/lib-form';
import Toggle from '../../Shared/Toggle/toggle';
import InspectionModal from './InspectionModal';
import { ProjectContext } from '../../../contexts/ProjectContext';
import { AppDivisionContext } from '../../../contexts/AppDivisionContext';
import { getProjectConfigHelpers } from '../Forms/projectFormSchema';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import { toast } from 'react-toastify';
import { useTenantFeature } from '../../../hooks/features';
import './InspectionSettings.scss';
import './InspectionModal.scss';

interface InspectionForm {
  junction_id: number; // Junction ID from API
  template_id: number;
  inspection_form_type: string;
  inspection_types: string;
  name_of_inspection: string;
  inspector_name: string | null;
  inspector_id: number | null;
  interval: string;
  interval_id: number;
  interval_days: number | null;
  next_due_date: string;
  active: boolean;
  is_primary: boolean;
  compliance_required: boolean;
  is_ccr_project: boolean;
  finding_compliance_required: boolean;
  certification_timeline_enabled: boolean;
  has_scheduled_inspection: boolean;
  scheduled_status: string | null;
  ca_inspection_scheduler: boolean;
}

interface ApiResponse {
  success: boolean;
  data: InspectionForm[];
  count: number;
  message: string;
}

interface InspectionSettingsData {
  id: number;
  inspectionFormType: string;
  name: string;
  inspector: string;
  inspectionInterval: string;
  nextDueDate: string;
  include: boolean;
  ca_inspection_scheduler: boolean;
}

interface InspectionSettingsProps {
  projectId?: number;
}

const InspectionSettings: React.FC<InspectionSettingsProps> = ({ projectId }) => {
  const projectStore = useContext(ProjectContext);
  const appDivisionContext = useContext(AppDivisionContext);
  const tenantFeatureQuery = useTenantFeature();
  const { hasAnyInspections, hasInAppInspections } = getProjectConfigHelpers(
    (projectStore.project?.project_config || 'full') as any,
  );
  const inspectorOnly = !hasAnyInspections;
  const [inspectionSettings, setInspectionSettings] = useState<InspectionSettingsData[]>([]);
  const [originalApiData, setOriginalApiData] = useState<InspectionForm[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<InspectionSettingsData | null>(null);

  // Check if "Enable multiple forms per project" feature is enabled
  const multipleFormsFeature = tenantFeatureQuery.data?.find(
    (feature: any) => feature.feature.name === 'Enable multiple forms per project'
  );
  const isMultipleFormsEnabled = multipleFormsFeature?.is_enabled || false;
  const maxInspectionTemplates = multipleFormsFeature?.max_inspection_templates || 1;

  // Check if the user has reached the maximum number of inspection templates
  const hasReachedMaxTemplates = originalApiData.length >= maxInspectionTemplates;

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()).toLocaleDateString();
  };

  const convertApiDataToSettings = (apiData: InspectionForm[]): InspectionSettingsData[] => {
    return apiData.map((form) => ({
      id: form.template_id,
      inspectionFormType: form.inspection_types,
      name: form.name_of_inspection,
      inspector: form.inspector_name || 'Unassigned',
      inspectionInterval: form.interval_days ? `${form.interval_days} days` : form.interval,
      nextDueDate: form.next_due_date,
      include: form.active,
      ca_inspection_scheduler: form.ca_inspection_scheduler,
    }));
  };

  const fetchInspectionForms = async () => {
    const currentProjectId = projectId || projectStore.project?.id;

    if (!currentProjectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data: ApiResponse = await schedulerApi.getProjectForms(currentProjectId);

      if (data.success) {
        setOriginalApiData(data.data);
        const convertedData = convertApiDataToSettings(data.data);

        setInspectionSettings(convertedData);
      } else {
        setError('Failed to fetch inspection forms');
      }
    } catch (err) {
      setError('Error fetching inspection forms');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInspectionForms();
  }, [projectId, projectStore.project?.id]);

  const handleToggleInclude = async (id: number) => {
    try {
      const originalInspection = originalApiData.find(item => item.template_id === id);

      if (!originalInspection?.junction_id) {
        console.error('Junction ID not found for template_id:', id);
        return;
      }

      const currentInspection = inspectionSettings.find(item => item.id === id);

      if (!currentInspection) {
        return;
      }

      const newActiveState = !currentInspection.include;

      const response = await schedulerApi.updateRoutineSettings(originalInspection.junction_id, {
        is_active: newActiveState
      });

      if (response?.success) {
        setInspectionSettings(prev =>
          prev.map(item =>
            item.id === id ? { ...item, include: newActiveState } : item
          )
        );
      }
    } catch (error) {
      console.error('Error updating inspection status:', error);
      setError('Error updating inspection status');
    }
  };

  const handleToggleSchedulerForm = async (id: number) => {
    try {
      const originalInspection = originalApiData.find(item => item.template_id === id);

      if (!originalInspection?.junction_id) {
        console.error('Junction ID not found for template_id:', id);
        return;
      }

      const currentInspection = inspectionSettings.find(item => item.id === id);

      if (!currentInspection) {
        return;
      }

      const newSchedulerState = !currentInspection.ca_inspection_scheduler;

      // If enabling scheduler form, disable any other form that is currently set as scheduler
      if (newSchedulerState) {
        const existingScheduler = originalApiData.find(item => item.ca_inspection_scheduler && item.template_id !== id);
        if (existingScheduler) {
          // Disable the existing scheduler form first
          const disableResponse = await schedulerApi.updateRoutineSettings(existingScheduler.junction_id, {
            ca_inspection_scheduler: false
          });

          if (!disableResponse?.success) {
            toast.error('Failed to disable the current scheduler form');
            return;
          }

          // Update local state to reflect the disabled scheduler
          setOriginalApiData(prev =>
            prev.map(item =>
              item.template_id === existingScheduler.template_id
                ? { ...item, ca_inspection_scheduler: false }
                : item
            )
          );

          setInspectionSettings(prev =>
            prev.map(item =>
              item.id === existingScheduler.template_id
                ? { ...item, ca_inspection_scheduler: false }
                : item
            )
          );
        }
      }

      const response = await schedulerApi.updateRoutineSettings(originalInspection.junction_id, {
        ca_inspection_scheduler: newSchedulerState
      });

      if (response?.success) {
        toast.success(`Scheduler form ${newSchedulerState ? 'enabled' : 'disabled'} successfully`);

        // Update local state
        setOriginalApiData(prev =>
          prev.map(item =>
            item.template_id === id
              ? { ...item, ca_inspection_scheduler: newSchedulerState }
              : item
          )
        );

        setInspectionSettings(prev =>
          prev.map(item =>
            item.id === id ? { ...item, ca_inspection_scheduler: newSchedulerState } : item
          )
        );
      }
    } catch (error) {
      console.error('Error updating scheduler form status:', error);
      setError('Error updating scheduler form status');
    }
  };

  const handleEdit = (id: number) => {
    const inspection = inspectionSettings.find(item => item.id === id);

    if (inspection) {
      setSelectedInspection(inspection);
      setIsEditMode(true);
      setShowModal(true);
    }
  };

  const handleAddNew = () => {
    setSelectedInspection(null);
    setIsEditMode(false);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedInspection(null);
    setIsEditMode(false);
  };

  const handleModalSubmit = async (formData: any) => {
    try {
      if (isEditMode && selectedInspection) {
        const originalInspection = originalApiData.find(item => item.template_id === selectedInspection.id);

        if (!originalInspection?.junction_id) {
          setError('Cannot update inspection: Junction ID not found');
          return;
        }

        if (inspectorOnly) {
          const response = await schedulerApi.updateRoutineSettings(originalInspection.junction_id, {
            inspector_id: formData.inspectorName === 'unassigned' ? null : parseInt(formData.inspectorName),
            is_active: originalInspection.active,
          });

          if (response?.success) {
            toast.success('Inspector updated successfully');
            await fetchInspectionForms();
            handleModalClose();
          } else {
            const errorMessage = response?.message || 'Failed to update inspector';

            toast.error(errorMessage);
            setError(errorMessage);
          }

          return;
        }

        const newIntervalId = parseInt(formData.inspectionInterval);
        const intervalChanged = newIntervalId !== originalInspection.interval_id;

        const updateRequestBody: any = {
          template_id: parseInt(formData.inspectionTemplateName),
          interval_id: newIntervalId,
          inspector_id: formData.inspectorName === 'unassigned' ? null : parseInt(formData.inspectorName),
          is_primary: formData.isPrimary || false,
          // Preserve existing compliance_required if the form hasn't surfaced a toggle for it.
          // Sending the original value is a no-op on the backend; sending `false` here would
          // wipe the row's compliance flag on every edit.
          compliance_required: formData.complianceRequired ?? originalInspection.compliance_required,
          is_ccr_project:
            formData.isCcrProject ?? originalInspection.is_ccr_project,
          finding_compliance_required:
            formData.findingComplianceRequired ??
            originalInspection.finding_compliance_required,
          certification_timeline_enabled: formData.certificationTimelineEnabled || false,
          is_active: originalInspection.active
        };

        // Only include next_inspection_date if the interval did NOT change
        // When interval changes, the backend will auto-recalculate the next inspection date
        if (!intervalChanged && formData.nextInspectionDate) {
          updateRequestBody.next_inspection_date = formData.nextInspectionDate;
        }

        const response = await schedulerApi.updateRoutineSettings(originalInspection.junction_id, updateRequestBody);

        if (response?.success) {
          if (intervalChanged) {
            toast.success('Inspection updated successfully. Next inspection date has been auto-recalculated based on the new interval.');
          } else {
            toast.success('Inspection updated successfully');
          }
          await fetchInspectionForms();
          handleModalClose();
        } else {
          const errorMessage = response?.message || 'Failed to update inspection';
          toast.error(errorMessage);
          setError(errorMessage);
        }
      } else {
        const currentProjectId = projectId || projectStore.project?.id;

        if (!currentProjectId) {
          setError('Project ID is required to create inspection');
          return;
        }

        // Omit `compliance_required`: the backend defaults it from the template's
        // has_compliance_cert when the field isn't sent. Sending `false` here would
        // override that smart default for compliance-capable templates.
        const requestBody: any = {
          project_id: currentProjectId,
          template_id: parseInt(formData.inspectionTemplateName),
          interval_id: parseInt(formData.inspectionInterval),
          inspector_id: formData.inspectorName === 'unassigned' ? null : parseInt(formData.inspectorName),
          next_inspection_date: formData.nextInspectionDate,
          is_active: true,
          is_primary: false,
          certification_timeline_enabled: false,
        };

        if (formData.complianceRequired !== undefined) {
          requestBody.compliance_required = formData.complianceRequired;
        }

        if (formData.isCcrProject !== undefined) {
          requestBody.is_ccr_project = formData.isCcrProject;
        }

        if (formData.findingComplianceRequired !== undefined) {
          requestBody.finding_compliance_required =
            formData.findingComplianceRequired;
        }

        const response =
          await schedulerApi.createProjectInspectionTemplate(requestBody);

        if (response?.success) {
          toast.success('Inspection created successfully');
          await fetchInspectionForms();
          handleModalClose();
        } else {
          const errorMessage = response?.message || 'Failed to create inspection';
          toast.error(errorMessage);
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error('Error saving inspection:', err);

      const errorMessage = err?.response?.data?.message || err?.message || 'Error saving inspection';

      toast.error(errorMessage);
      setError(errorMessage);
    }
  };

  const getModalInitialData = () => {
    if (!selectedInspection) return {};

    const originalInspection = originalApiData.find(item => item.template_id === selectedInspection.id);

    if (!originalInspection) {
      return {};
    }

    const initialData = {
      inspectionTemplateName: originalInspection.template_id?.toString() ?? '',
      inspectionTemplateType: '',
      inspectionInterval: originalInspection.interval_id?.toString() ?? '',
      inspectorName: originalInspection.inspector_id ? originalInspection.inspector_id.toString() : 'unassigned',
      nextInspectionDate: originalInspection.next_due_date ? originalInspection.next_due_date.split('T')[0] : '',
      isPrimary: originalInspection.is_primary || false,
      complianceRequired: originalInspection.compliance_required || false,
      isCcrProject: originalInspection.is_ccr_project || false,
      findingComplianceRequired:
        originalInspection.finding_compliance_required || false,
      certificationTimelineEnabled: originalInspection.certification_timeline_enabled || false,
    };

    return initialData;
  };

  return (
    <div id="inspectionSettings" className="inspection-settings">
      <div
        className="inspection-settings__header"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h3>{inspectorOnly ? 'Inspector' : 'Inspection Settings'}</h3>
        <i
          className={
            isCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'
          }
          aria-hidden="true"
        />
      </div>
      <hr />
      {isCollapsed && (
        <div className="inspection-settings__content">
          {isLoading ? (
            <div className="loading-state">
              <p>Loading inspection forms...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <Button
                onClick={fetchInspectionForms}
                style={{
                  marginTop: '0.5rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '0.5rem 1rem'
                }}
              >
                Retry
              </Button>
            </div>
          ) : (
            <>
              {isMultipleFormsEnabled && hasInAppInspections && (
                <div className='inspection-settings__actions'>
                  <Tooltip
                    title={
                      hasReachedMaxTemplates
                        ? `Maximum limit of ${maxInspectionTemplates} inspection template${maxInspectionTemplates > 1 ? 's' : ''} reached`
                        : ''
                    }
                    placement='top'
                    arrow
                  >
                    <span>
                      <Button
                        onClick={handleAddNew}
                        disabled={hasReachedMaxTemplates}
                        style={{
                          marginBottom: '1rem',
                          backgroundColor: hasReachedMaxTemplates
                            ? '#cccccc'
                            : '#007bff',
                          color: 'white',
                          border: 'none',
                          padding: '0.5rem 1rem',
                          cursor: hasReachedMaxTemplates
                            ? 'not-allowed'
                            : 'pointer',
                          opacity: hasReachedMaxTemplates ? 0.6 : 1,
                        }}
                      >
                        Add New Inspection
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              )}
              {inspectionSettings.length === 0 ? (
                <div className="no-data-state">
                  <p>No inspection forms found for this project.</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <Table className="inspection-settings__table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Inspection Form Type</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Inspector</TableCell>
                        <TableCell>Inspection Interval</TableCell>
                        <TableCell>Next Due Date</TableCell>
                        <TableCell>Include</TableCell>
                        {projectStore.project?.forecast_weather && projectStore.project.ca_inspection_scheduler &&
                          (<TableCell>Scheduler Form</TableCell>)}
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inspectionSettings.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{inspectorOnly ? 'None' : item.inspectionFormType}</TableCell>
                          <TableCell>{inspectorOnly ? 'None' : item.name}</TableCell>
                          <TableCell>{item.inspector}</TableCell>
                          <TableCell>{item.inspectionInterval}</TableCell>
                          <TableCell>{inspectorOnly ? 'N/A' : formatDate(item.nextDueDate)}</TableCell>
                          <TableCell>
                            <Toggle
                              isChecked={item.include}
                              handleChange={() => handleToggleInclude(item.id)}
                              disabled={originalApiData.find(orig => orig.template_id === item.id)?.is_primary || false}
                            />
                          </TableCell>
                          {projectStore.project?.forecast_weather && projectStore.project.ca_inspection_scheduler && (
                            <TableCell>
                              <Toggle
                                isChecked={item.ca_inspection_scheduler}
                                handleChange={() => handleToggleSchedulerForm(item.id)}
                              />
                            </TableCell>)}
                          <TableCell>
                            <Button
                              onClick={() => handleEdit(item.id)}
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.875rem' }}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Mobile Card View */}
                  <div className="inspection-settings__card-view">
                    {inspectionSettings.map((item) => (
                      <div key={item.id} className='inspection-settings__card'>
                        <div className='inspection-settings__card-row'>
                          <span className='inspection-settings__card-row-label'>
                            Form Type:
                          </span>
                          <span className='inspection-settings__card-row-value'>
                            {inspectorOnly ? 'None' : item.inspectionFormType}
                          </span>
                        </div>
                        <div className='inspection-settings__card-row'>
                          <span className='inspection-settings__card-row-label'>
                            Name:
                          </span>
                          <span className='inspection-settings__card-row-value'>
                            {inspectorOnly ? 'None' : item.name}
                          </span>
                        </div>
                        <div className="inspection-settings__card-row">
                          <span className="inspection-settings__card-row-label">Inspector:</span>
                          <span className="inspection-settings__card-row-value">{item.inspector}</span>
                        </div>
                        <div className="inspection-settings__card-row">
                          <span className="inspection-settings__card-row-label">Interval:</span>
                          <span className="inspection-settings__card-row-value">{item.inspectionInterval}</span>
                        </div>
                        <div className='inspection-settings__card-row'>
                          <span className='inspection-settings__card-row-label'>
                            Next Due Date:
                          </span>
                          <span className='inspection-settings__card-row-value'>
                            {inspectorOnly ? 'N/A' : formatDate(item.nextDueDate)}
                          </span>
                        </div>
                        <div className="inspection-settings__card-row">
                          <span className="inspection-settings__card-row-label">Include:</span>
                          <span className="inspection-settings__card-row-value">
                            <Toggle
                              isChecked={item.include}
                              handleChange={() => handleToggleInclude(item.id)}
                              disabled={originalApiData.find(orig => orig.template_id === item.id)?.is_primary || false}
                            />
                          </span>
                        </div>
                        {projectStore.project?.forecast_weather && projectStore.project.ca_inspection_scheduler && (
                          <div className="inspection-settings__card-row">
                            <span className="inspection-settings__card-row-label">Scheduler Form:</span>
                            <span className="inspection-settings__card-row-value">
                              <Toggle
                                isChecked={item.ca_inspection_scheduler}
                                handleChange={() => handleToggleSchedulerForm(item.id)}
                              />
                            </span>
                          </div>)}
                        <div className="inspection-settings__card-actions">
                          <Button
                            onClick={() => handleEdit(item.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              fontSize: '0.875rem',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none'
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Modal for adding/editing inspections */}
      {showModal && (
        <FormContextProvider>
          <InspectionModal
            show={showModal}
            onClose={handleModalClose}
            onSubmit={handleModalSubmit}
            isEdit={isEditMode}
            initialData={getModalInitialData()}
            projectId={projectId || projectStore.project?.id}
            divisionId={appDivisionContext.appDivisionId || 1}
            existingInspectionTypes={originalApiData.map(item => item.inspection_types)}
            routineId={
              isEditMode && selectedInspection
                ? originalApiData.find(r => r.template_id === selectedInspection.id)?.junction_id
                : undefined
            }
            junctionId={isEditMode && selectedInspection
              ? originalApiData.find(item => item.template_id === selectedInspection.id)?.junction_id
              : undefined}
            inspectorOnly={inspectorOnly}
          />
        </FormContextProvider>
      )}
    </div>
  );
};

export default InspectionSettings;